import React from 'react';
import { SparkDialog } from 'sharedComponents/UI';
import SparkFormStepper from 'sharedComponents/UI/SparkStepper';
import CreatorBasicInfoForm, {
  CreatorBasicInfoFormValues,
} from './CreatorBasicInfoForm';
import CreatorProfessionalInfoForm, {
  CreatorProfessionalInfoFormValues,
} from './CreatorProfessionalInfoForm';
import CreatorProfileForm, {
  CreatorProfileFormValues,
} from './CreatorProfileForm';
import { useAddMarketPlaceAccount } from 'hooks/Marketplace';
import { useWorkspaceStore } from 'stores';

type CreateProfileStepperFormProps = {
  open: boolean;
  onClose: () => void;
};

type CollectiveValuesType = {
  1?: CreatorBasicInfoFormValues;
  2?: CreatorProfessionalInfoFormValues;
  3?: CreatorProfileFormValues;
};

function CreateProfileStepperForm({
  open,
  onClose,
}: CreateProfileStepperFormProps) {
  const [activeStep, setActiveStep] = React.useState<number>(1);
  const collectiveValues = React.useRef<CollectiveValuesType | null>(null);
  const { mutateAsync: addMarketplaceAccount } = useAddMarketPlaceAccount();
  const { workspace } = useWorkspaceStore();

  const setBasicInfoCollection = (formValues: CreatorBasicInfoFormValues) => {
    const previous = collectiveValues.current || {};
    collectiveValues.current = { ...previous, 1: formValues };
  };

  const setProfessionalInfoCollection = (
    formValues: CreatorProfessionalInfoFormValues
  ) => {
    const previous = collectiveValues.current || {};
    collectiveValues.current = { ...previous, 2: formValues };
  };

  const setCreatorProfileCollection = (
    formValues: CreatorProfileFormValues
  ) => {
    const previous = collectiveValues.current || {};
    collectiveValues.current = { ...previous, 3: formValues };
  };

  const handleSubmit = async () => {
    if (!collectiveValues.current) {
      return;
    }
    const {
      1: basicInfo,
      2: professionalInfo,
      3: creatorProfile,
    } = collectiveValues.current;

    if (!basicInfo || !professionalInfo || !creatorProfile) {
      return;
    }

    await addMarketplaceAccount({
      ...basicInfo,
      ...professionalInfo,
      ...creatorProfile,
      accountID: workspace?.attributes.accountID || '',
      returnURL: '',
      refreshURL: '',
    });
    onClose();
  };

  const nextStepHandler = async () => {
    if (activeStep < 3) {
      setActiveStep((prev) => prev + 1);
    }
    if (activeStep === 3) {
      await handleSubmit();
    }
  };

  const previousStepHandler = () => {
    setActiveStep((prev) => {
      if (prev === 1) {
        return prev;
      }
      return prev - 1;
    });
  };

  const getCurrentStep = () => {
    switch (activeStep) {
      case 1:
        return (
          <CreatorBasicInfoForm
            activeStep={activeStep}
            nextStepHandler={nextStepHandler}
            setBasicInfoCollection={setBasicInfoCollection}
            basicInfoCollection={collectiveValues.current?.[1]}
          />
        );
      case 2:
        return (
          <CreatorProfessionalInfoForm
            nextStepHandler={nextStepHandler}
            previousStepHandler={previousStepHandler}
            activeStep={activeStep}
            professionalInfoCollection={collectiveValues.current?.[2]}
            setProfessionalInfoCollection={setProfessionalInfoCollection}
          />
        );
      case 3:
        return (
          <CreatorProfileForm
            nextStepHandler={nextStepHandler}
            previousStepHandler={previousStepHandler}
            activeStep={activeStep}
            creatorProfileCollection={collectiveValues.current?.[3]}
            setCreatorProfileCollection={setCreatorProfileCollection}
          />
        );
      default:
        return <></>;
    }
  };
  return (
    <SparkDialog open={open} onClose={onClose} title='Creator'>
      <SparkFormStepper stepIDs={[1, 2, 3]} activeStep={activeStep} />
      <div>{getCurrentStep()}</div>
    </SparkDialog>
  );
}

export default CreateProfileStepperForm;
